import { AbstractControl, ValidationErrors } from '@angular/forms';

export type MultiBuyOfferStatus = 'active' | 'scheduled' | 'expired' | 'disabled';
export type PriceScheduleStatus = 'scheduled' | 'active' | 'superseded' | 'cancelled' | 'expired';

export interface MultiBuyOfferStatusInfo {
  status: MultiBuyOfferStatus;
  label: string;
  badgeClass: string;
}

export function requiredQuantityValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (val === null || val === undefined || val === '') {
    return { requiredQuantity: { message: 'Required quantity is required' } };
  }
  const num = Number(val);
  if (!Number.isInteger(num) || num < 2) {
    return { requiredQuantity: { message: 'Must be a whole number of 2 or more' } };
  }
  return null;
}

export function offerPriceValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (val === null || val === undefined || val === '') {
    return { offerPrice: { message: 'Offer price is required' } };
  }
  const num = Number(val);
  if (!Number.isFinite(num) || num < 0) {
    return { offerPrice: { message: 'Offer price must be 0 or greater' } };
  }
  return null;
}

export function scheduledPriceValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (val === null || val === undefined || val === '') {
    return { scheduledPrice: { message: 'Scheduled price is required' } };
  }
  const num = Number(val);
  if (!Number.isFinite(num) || num <= 0) {
    return { scheduledPrice: { message: 'Scheduled price must be greater than 0' } };
  }
  return null;
}

export function validateScheduleDeactivatesAt(
  deactivatesAtLocal: string | null | undefined,
  activatesAtLocal: string | null | undefined
): string | null {
  if (!deactivatesAtLocal) {
    return null;
  }
  const deactivates = new Date(deactivatesAtLocal);
  if (Number.isNaN(deactivates.getTime())) {
    return 'Invalid end date and time';
  }
  if (deactivates.getTime() <= Date.now()) {
    return 'End must be in the future';
  }
  if (activatesAtLocal) {
    const activates = new Date(activatesAtLocal);
    if (!Number.isNaN(activates.getTime()) && deactivates.getTime() <= activates.getTime()) {
      return 'End must be after activation';
    }
  }
  return null;
}

export function futureDateTimeValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (!val) {
    return { futureDate: { message: 'Activation date and time is required' } };
  }
  const date = new Date(val);
  if (Number.isNaN(date.getTime())) {
    return { futureDate: { message: 'Invalid date and time' } };
  }
  if (date.getTime() <= Date.now()) {
    return { futureDate: { message: 'Activation must be in the future' } };
  }
  return null;
}

export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function datetimeLocalToIso(local: string | null | undefined): string {
  if (!local) {
    return '';
  }
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString();
}

export function formatPricingDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

export function getDiscountGroupOfferStatus(offer: any, now: Date = new Date()): MultiBuyOfferStatusInfo {
  if (offer?.isActive === false) {
    return { status: 'disabled', label: 'Inactive', badgeClass: 'bg-secondary' };
  }

  if (offer?.isCurrentlyActive === true) {
    return { status: 'active', label: 'Active', badgeClass: 'bg-success' };
  }

  const validFrom = offer?.validFrom ? new Date(offer.validFrom) : null;
  const validTo = offer?.validTo ? new Date(offer.validTo) : null;

  if (validFrom && !Number.isNaN(validFrom.getTime()) && validFrom.getTime() > now.getTime()) {
    return { status: 'scheduled', label: 'Scheduled', badgeClass: 'bg-info text-dark' };
  }

  if (validTo && !Number.isNaN(validTo.getTime()) && validTo.getTime() < now.getTime()) {
    return { status: 'expired', label: 'Expired', badgeClass: 'bg-dark' };
  }

  if (offer?.isCurrentlyActive === false) {
    return { status: 'disabled', label: 'Inactive', badgeClass: 'bg-secondary' };
  }

  return getMultiBuyOfferStatus(offer, now);
}

export function getMultiBuyOfferStatus(offer: any, now: Date = new Date()): MultiBuyOfferStatusInfo {
  if (offer?.isActive === false) {
    return { status: 'disabled', label: 'Disabled', badgeClass: 'bg-secondary' };
  }

  const validFrom = offer?.validFrom ? new Date(offer.validFrom) : null;
  const validTo = offer?.validTo ? new Date(offer.validTo) : null;

  if (validFrom && !Number.isNaN(validFrom.getTime()) && validFrom.getTime() > now.getTime()) {
    return { status: 'scheduled', label: 'Scheduled', badgeClass: 'bg-info text-dark' };
  }

  if (validTo && !Number.isNaN(validTo.getTime()) && validTo.getTime() < now.getTime()) {
    return { status: 'expired', label: 'Expired', badgeClass: 'bg-dark' };
  }

  return { status: 'active', label: 'Active', badgeClass: 'bg-success' };
}

export function getPriceScheduleStatusInfo(status: string): { label: string; badgeClass: string } {
  switch (status) {
    case 'scheduled':
      return { label: 'Scheduled', badgeClass: 'bg-info text-dark' };
    case 'active':
      return { label: 'Active', badgeClass: 'bg-success' };
    case 'superseded':
      return { label: 'Superseded', badgeClass: 'bg-secondary' };
    case 'cancelled':
      return { label: 'Cancelled', badgeClass: 'bg-danger' };
    case 'expired':
      return { label: 'Expired', badgeClass: 'bg-secondary' };
    default:
      return { label: status || 'Unknown', badgeClass: 'bg-secondary' };
  }
}

export function formatMultiBuyOfferSummary(offer: any, currency: string = 'DKK'): string {
  const qty = offer?.requiredQuantity ?? '?';
  const price = offer?.offerPrice ?? '?';
  return `${qty} for ${price} ${currency}`;
}

export function isDuplicateActivatesAtError(message: string | null | undefined): boolean {
  if (!message) {
    return false;
  }
  const lower = message.toLowerCase();
  return lower.includes('activatesat') || lower.includes('activation') || lower.includes('same time') || lower.includes('already scheduled');
}
